import { Tag, Typography } from 'antd';
import React, { useCallback } from 'react';
import http from 'utils/http';
import { EMPTY_OBJECT } from 'constants';
import { getWorkflowItemIcon } from 'utils/workflow';
import dayjs from 'dayjs';
import { formatAmount } from 'utils';

export const getColumns = ({
  hasStatus,
  hasTrigger,
}) => [
  {
    title: 'NAME',
    key: 'name',
    width: 350,
    ellipsis: false,
    render: (_, record, index) => {
      // Extract action positions from the actions array
      const actionPositions = record.actions || [];

      // Sort by position to ensure correct order
      const sortedActions = actionPositions.sort((a, b) => a.position - b.position);

      return (
        <div
          style={{ display: 'flex', gap: '8px', cursor: 'pointer' }}
        >
          <Typography.Text
            style={{ cursor: 'pointer' }}

          >
            {record.name}
          </Typography.Text>
          {sortedActions.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center',
            }}
            >
              {
                record.trigger.auto_trigger && (
                  <div>
                    {getWorkflowItemIcon('trigger')}
                  </div>
                )
              }
              {sortedActions.map((action) => (
                <div key={`${record.id}-action-${action.id}`}>
                  {getWorkflowItemIcon(action, { waitIcon: false })}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    },
  },
  hasStatus && {
    title: 'STATUS',
    key: 'status',
    width: 120,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Tag color={record.is_active ? 'green' : 'default'} bordered style={{ borderStyle: 'solid' }}>
        {record.is_active ? 'Active' : 'Inactive'}
      </Tag>
    ),
  },
  hasTrigger && {
    title: 'TRIGGER',
    key: 'trigger',
    width: 120,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Typography.Text className="capitalize">{record.trigger.code.split('_').join(' ')}</Typography.Text>
    ),
  },
  {
    title: 'TOTAL RUNS',
    key: 'total_runs',
    width: 120,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Typography.Text>{record.total_runs || 0}</Typography.Text>
    ),
  },
  {
    title: 'REVENUE GENERATED',
    key: 'revenue_generated',
    width: 150,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Typography.Text className="whitespace-nowrap">{record.revenue_generated ? `${formatAmount(record.revenue_generated)}` : '-'}</Typography.Text>
    ),
  },
  {
    title: 'OPEN RATE',
    key: 'open_rate',
    width: 120,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Typography.Text>{record.open_rate ? `${record.open_rate}%` : '-'}</Typography.Text>
    ),
  },
  {
    title: 'CLICK RATE',
    key: 'click_rate',
    width: 120,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Typography.Text>{record.click_rate ? `${record.click_rate}%` : '-'}</Typography.Text>
    ),
  },
  {
    title: 'REPLY RATE',
    key: 'reply_rate',
    width: 120,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Typography.Text>{record.reply_rate ? `${record.reply_rate}%` : '-'}</Typography.Text>
    ),
  },
  {
    title: 'LAST ACTIVITY',
    key: 'last_activity',
    width: 150,
    ellipsis: false,
    render: (_, record) => {
      if (!record.last_activity) {
        return <Typography.Text type="secondary">-</Typography.Text>;
      }

      const date = new Date(record.last_activity);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      return <Typography.Text>{`${formattedDate}, ${formattedTime}`}</Typography.Text>;
    },
  },
].filter(Boolean);

export const useFetcher = ({
  filters,
  resetPageRef,
  setLoading,
  setWorkflows,
  setCount,
  queryParams = EMPTY_OBJECT,
}) => useCallback((pagination, filter, sorter) => {
  const page = filters.search || resetPageRef.current ? 1 : pagination.current;
  const queries = {
    page,
    search: filters.search,
    page_size: pagination.pageSize,
    ordering: '-created_at',
    email_from: filters.email_from,
    ...queryParams,
  };
  if (sorter?.order && sorter?.columnKey) {
    queries.ordering = `${sorter?.order === 'descend' ? '-' : ''}${sorter?.columnKey}`;
  }
  if (filters.run_date?.range?.length > 0) {
    queries.run_date_after = dayjs(filters.run_date.range[0]).format('YYYY-MM-DD');
    queries.run_date_before = dayjs(filters.run_date.range[1]).format('YYYY-MM-DD');
  }

  resetPageRef.current = false;
  setLoading(true);
  return http.get('/v1/organizations/{{organizationId}}/workflow-report/', { params: queries })
    .then(({ data: { count, results } } = {}) => {
      setWorkflows(results);
      setCount(count);
      return {
        page,
        data: results,
        total: count,
      };
    }).finally(() => setLoading(false));
}, [filters]);
